import React from 'react';
import { motion } from 'framer-motion';
import { VestingSimulator } from '@/components/charts/VestingSimulator';
import { Clock, TrendingUp, Lock, Calendar } from 'lucide-react';

export const VestingSection = () => {
  return (
    <section id="vesting" className="py-24 relative">
      {/* Background */}
      <div className="absolute inset-0 neural-grid opacity-20" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Vesting Schedule
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
            Unlock & Vesting Simulator
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore how the circulating supply evolves over time. 
            Use the interactive timeline to simulate different months and see unlock schedules.
          </p>
        </motion.div>
        
        {/* Key Vesting Info Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
        >
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Investor Cliff</p>
                <p className="text-lg font-bold text-foreground">6 Months</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              No investor tokens unlock before month 6
            </p>
          </div>
          
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Investor Vesting</p>
                <p className="text-lg font-bold text-foreground">12 Months</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Linear unlock from month 6 to month 18
            </p>
          </div>
          
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Team Cliff</p>
                <p className="text-lg font-bold text-foreground">24 Months</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Team tokens fully locked for 2 years
            </p>
          </div>
          
          <div className="glass rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Team Vesting</p>
                <p className="text-lg font-bold text-foreground">36 Months</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Linear unlock from month 24 to month 60
            </p>
          </div>
        </motion.div>
        
        {/* Simulator */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <VestingSimulator />
        </motion.div>
        
        {/* Neural line separator */}
        <div className="mt-24 neural-line" />
      </div>
    </section>
  );
};

export default VestingSection;
